import { CONFIG_MODES, layoutKindFor, type AreaLayout, type ConfigMode, type Section } from './editor-state.js'

type CreateSectionParams = Pick<Section, 'id' | 'name'> & {
  dropAreaType: NonNullable<Section['dropAreaType']>
  children_strategy?: Section['children_strategy']
}

const createSection = (overrides: CreateSectionParams): Section => ({
  id: overrides.id,
  name: overrides.name,
  children_strategy: overrides.children_strategy ?? 'and',
  widgets: [],
  dropAreaType: overrides.dropAreaType,
  maxWidgets: 5,
})

// Each dynamic area starts with exactly 1 zone (full width) — more are added on demand via
// addRow/addColumn, up to MAX_ROWS/MAX_COLUMNS_PER_ROW.
export const createDefaultZoneSection = (id: string, name: string, dropAreaType: 'product-info' | 'product-footer'): Section =>
  createSection({ id, name, dropAreaType, children_strategy: dropAreaType === 'product-footer' ? 'or' : 'and' })

export const createDefaultAreaLayout = (): AreaLayout => ({
  productInfo: [['product-info-zone-1']],
  orderActions: [['order-actions-zone-1']],
})

const dynamicZoneSections = (): Section[] => [
  createDefaultZoneSection('product-info-zone-1', 'Zone 1', 'product-info'),
  createDefaultZoneSection('order-actions-zone-1', 'Zone 1', 'product-footer'),
]

export const createDefaultSections = (): Section[] => [
  createSection({ id: 'top-left', name: 'Top Left', dropAreaType: 'image-overlay' }),
  createSection({ id: 'top-center', name: 'Top Center', dropAreaType: 'image-overlay' }),
  createSection({ id: 'top-right', name: 'Top Right', dropAreaType: 'image-overlay' }),
  createSection({ id: 'middle-left', name: 'Middle Left', dropAreaType: 'image-overlay' }),
  createSection({ id: 'middle-right', name: 'Middle Right', dropAreaType: 'image-overlay' }),
  createSection({ id: 'bottom-left', name: 'Bottom Left', dropAreaType: 'image-overlay' }),
  createSection({ id: 'bottom-center', name: 'Bottom Center', dropAreaType: 'image-overlay' }),
  createSection({ id: 'bottom-right', name: 'Bottom Right', dropAreaType: 'image-overlay' }),
  ...dynamicZoneSections(),
]

export const createListDefaultSections = (): Section[] => [
  // Real (if always-singular) dynamic zones, unlike the 8 fixed corners below — see
  // ImageFlowZoneCfg and the 'image-flow' dropAreaType.
  createSection({ id: 'above', name: 'Above', dropAreaType: 'image-flow' }),
  createSection({ id: 'top-left', name: 'Top Left', dropAreaType: 'image-overlay' }),
  createSection({ id: 'top-center', name: 'Top Center', dropAreaType: 'image-overlay' }),
  createSection({ id: 'top-right', name: 'Top Right', dropAreaType: 'image-overlay' }),
  createSection({ id: 'middle-left', name: 'Middle Left', dropAreaType: 'image-overlay' }),
  createSection({ id: 'middle-right', name: 'Middle Right', dropAreaType: 'image-overlay' }),
  createSection({ id: 'bottom-left', name: 'Bottom Left', dropAreaType: 'image-overlay' }),
  createSection({ id: 'bottom-center', name: 'Bottom Center', dropAreaType: 'image-overlay' }),
  createSection({ id: 'bottom-right', name: 'Bottom Right', dropAreaType: 'image-overlay' }),
  createSection({ id: 'below', name: 'Below', dropAreaType: 'image-flow' }),
  ...dynamicZoneSections(),
]

export const defaultSectionsFor = (mode: ConfigMode): Section[] =>
  layoutKindFor(mode) === 'list' ? createListDefaultSections() : createDefaultSections()

export const createDefaultSectionsByMode = (): Record<ConfigMode, Section[]> =>
  Object.fromEntries(CONFIG_MODES.map(mode => [mode, defaultSectionsFor(mode)])) as Record<ConfigMode, Section[]>

export const createDefaultAreaLayoutByMode = (): Record<ConfigMode, AreaLayout> =>
  Object.fromEntries(CONFIG_MODES.map(mode => [mode, createDefaultAreaLayout()])) as Record<ConfigMode, AreaLayout>
